import {
  SearchRun,
  type HotelProduct,
  type ProductSearch,
  type ProductSearchConfig,
  type ProductSearchEvents,
  type ProductSearchOptions,
  type ProductSearchProgress,
  type ProductSearchRequest,
  type ProductSearchResult,
  type ProductSearchState,
  type ProductSearchTimings,
  type SearchCriteria,
} from '@/entities/product/@/tour';
import { ProductType } from '@/shared/config/pages';
import { getClientUuid } from '@/shared/lib/clientUuid';
import { createUuid } from '@/shared/lib/createUuid';
import { TypedEventEmitter } from '@/shared/lib/TypedEventEmitter';
import type { TourProductDTO } from '@/shared/model';
import TourProduct from './TourProduct';
import {
  TOUR_SEARCH_CACHE_PATH,
  TOUR_SEARCH_GROUPED_CACHE_PATH,
  TOUR_SEARCH_LIMITS,
  TOUR_SEARCH_SECTIONS,
} from '../config/search';

type TourSearchEvents = ProductSearchEvents<TourProduct>;

const EMPTY_PROGRESS: ProductSearchProgress = { jobsCount: 0, jobsSuccess: 0, operators: {} };
const EMPTY_TIMINGS: ProductSearchTimings = { startedAt: null, firstResultsAt: null, finishedAt: null };

function resolveTourSearchEndpoints({ grouped }: ProductSearchRequest) {
  const cache = grouped ? TOUR_SEARCH_GROUPED_CACHE_PATH : TOUR_SEARCH_CACHE_PATH;

  return { cache, session: `${cache}/asyncSearch` };
}

export default class TourSearch implements ProductSearch {
  private readonly config: ProductSearchConfig<TourProduct, TourProductDTO> = {
    type: ProductType.TourOffer,
    sections: TOUR_SEARCH_SECTIONS,
    limits: TOUR_SEARCH_LIMITS,
    resolveEndpoints: resolveTourSearchEndpoints,
    pickProducts: (result) => result.tours ?? [],
    // Цена тура для ключа дедупа — с топливным сбором, как и TourProduct.price.
    getProductPrice: (dto) => dto.price + (dto.oilTax || 0),
    createProduct: (dto, dictionaries) => new TourProduct(dto, dictionaries),
  };

  private readonly emitter = new TypedEventEmitter<TourSearchEvents>();

  private current: SearchRun<TourProduct, TourProductDTO> | null = null;

  get type(): ProductType {
    return this.config.type;
  }

  get state(): ProductSearchState {
    return this.current?.state ?? 'idle';
  }

  get searchUuid(): string | null {
    return this.current?.searchUuid ?? null;
  }

  get products(): TourProduct[] {
    return this.current?.products ?? [];
  }

  get hotels(): HotelProduct[] {
    return this.current?.hotels ?? [];
  }

  get productsByHotel(): Map<number, TourProduct[]> {
    return this.current?.productsByHotel ?? new Map();
  }

  get progress(): ProductSearchProgress {
    return { ...(this.current?.progress ?? EMPTY_PROGRESS) };
  }

  get timings(): ProductSearchTimings {
    return { ...(this.current?.timings ?? EMPTY_TIMINGS) };
  }

  run(criteria: SearchCriteria, options: ProductSearchOptions = {}): Promise<ProductSearchResult<TourProduct>> {
    this.abort();

    const run = new SearchRun<TourProduct, TourProductDTO>({
      emitter: this.emitter,
      config: this.config,
      criteria,
      options,
      searchUuid: options.searchUuid || createUuid(),
      clientUuid: getClientUuid(),
    });
    this.current = run;

    return run.execute({ withLowLimit: options.withLowLimit });
  }

  abort(): void {
    this.current?.abort();
  }

  on<K extends keyof TourSearchEvents>(event: K, listener: (payload: TourSearchEvents[K]) => void): () => void {
    return this.emitter.on(event, listener);
  }

  once<K extends keyof TourSearchEvents>(event: K, listener: (payload: TourSearchEvents[K]) => void): () => void {
    return this.emitter.once(event, listener);
  }

  off<K extends keyof TourSearchEvents>(event: K, listener: (payload: TourSearchEvents[K]) => void): void {
    this.emitter.off(event, listener);
  }
}
